import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges
} from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { NfePayload } from "@interfaces/nfe-payload";
import { Option } from "@interfaces/option";
import { Store } from "@ngrx/store";
import { CepService } from "@services/cep.service";
import { CompanyService } from "@services/company.service";
import { IbgeService } from "@services/ibge.service";
import { ModalService } from "@services/modal.service";
import { selectNfeBudget } from "@state/selectors/app.selectors";
import { selectPNCompany } from "@state/selectors/user.selectors";
import indIeDestOptions from "@static/indIeDest.json";
import tipoLogradouroOptions from "@static/tipoLogradouro.json";
import yesOrNotOptions from "@static/yesOrNot.json";
import {
  cpfCnpjValidator,
  codigoCidadeValidator,
  deriveIndIeDest,
  INSCRICAO_ESTADUAL_ERROR_MESSAGES,
  InscricaoEstadualError,
  inscricaoEstadualValidator,
  isCnpj,
  maxLengthValidator,
  normalizeIndIeDest
} from "src/app/utils/nfe.validators";
import { debounceTime, skip, Subscription } from "rxjs";

@Component({
  selector: "app-client-form",
  templateUrl: "./client-form.component.html",
  styleUrls: ["./client-form.component.scss"]
})
export class ClientFormComponent implements OnInit, OnDestroy, OnChanges {
  @Input() cliente?: NfePayload["destinatario"];
  @Input() showAddressLookupButton = false;
  /**
   * Limite da razão social/nome. Padrão 60 (xNome da NF-e); a NFS-e passa o
   * limite do layout municipal (ver `nfse-tomador.util`).
   */
  @Input() razaoSocialMaxLength = 60;
  @Output() data = new EventEmitter();
  @Output() status = new EventEmitter<boolean>();

  budget = this.store.selectSignal(selectNfeBudget);
  company = this.store.selectSignal(selectPNCompany);

  clientForm = new FormGroup({
    cpfCnpj: new FormControl(
      this.budget()?.cliente.cpfCnpj?.replaceAll(/\D/g, ""),
      [Validators.required, cpfCnpjValidator]
    ),
    razaoSocial: new FormControl(this.budget()?.cliente.nomeRazaoSocial, [
      Validators.required,
      maxLengthValidator(60)
    ]),
    email: new FormControl(this.budget()?.cliente.email?.[0]?.email, [
      Validators.required,
      Validators.email
    ]),
    inscricaoEstadual: new FormControl(
      this.budget()?.cliente.inscricaoEstadual,
      [inscricaoEstadualValidator]
    ),
    orgaoPublico: new FormControl(false),
    indicadorInscricaoEstadual: new FormControl(
      this.budget()?.cliente.indIeDest
    ),
    endereco: new FormGroup({
      bairro: new FormControl(this.budget()?.cliente.bairro, [
        Validators.required
      ]),
      cep: new FormControl(this.budget()?.cliente.cep, [Validators.required]),
      codigoCidade: new FormControl(
        this.budget()?.cliente.codigoIbge?.toString(),
        [Validators.required, codigoCidadeValidator]
      ),
      estado: new FormControl(this.budget()?.cliente.uf, [Validators.required]),
      logradouro: new FormControl(this.budget()?.cliente.logradouro, [
        Validators.required,
        maxLengthValidator(60)
      ]),
      numero: new FormControl(this.budget()?.cliente.numero, [
        Validators.required
      ]),
      tipoLogradouro: new FormControl(this.budget()?.cliente.tipoLogradouro, [
        Validators.required
      ]),
      descricaoCidade: new FormControl(this.budget()?.cliente.cidade, [
        Validators.required
      ]),
      complemento: new FormControl(this.budget()?.cliente.complemento)
    })
  });

  yesOrNot = yesOrNotOptions;
  indIeDestOptions = indIeDestOptions;
  tipoLogradouroOptions = tipoLogradouroOptions;

  states = signal<Option[]>([]);
  cities = signal<Option[]>([]);
  isSearchingAddress = signal(false);

  juridicPerson = signal(isCnpj(this.budget()?.cliente.cpfCnpj));

  $: Subscription[] = [];

  get endereco() {
    return this.clientForm.get("endereco");
  }

  constructor(
    private readonly store: Store,
    private readonly ibgeService: IbgeService,
    private readonly cepService: CepService,
    private readonly companyService: CompanyService,
    private readonly modalService: ModalService,
    private readonly destroyRef: DestroyRef
  ) {
    this.$.push(
      this.clientForm.controls.endereco.controls.estado.valueChanges.subscribe(
        (state) => {
          this.fetchCities(state ?? "");
        }
      )
    );

    this.destroyRef.onDestroy(() => {
      this.$.forEach(($) => $.unsubscribe());
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["razaoSocialMaxLength"]) {
      this.applyRazaoSocialMaxLength();
    }

    if (changes["cliente"]?.currentValue) {
      const form = this.clientForm;

      this.clientForm.patchValue(
        this.cliente as ReturnType<typeof form.getRawValue>
      );
      this.syncJuridicPerson();
    }
  }

  ngOnInit(): void {
    this.fetchStates();
    this.handleCepChange();
    this.handleCnpjChange();
    this.handleLogradouroChange();
    this.handleFormChange();
    this.syncJuridicPerson();

    this.data.emit(this.clientForm.value);
    this.status.emit(this.clientForm.status === "VALID");
  }

  ngOnDestroy(): void {
    this.$.forEach(($) => $.unsubscribe());
  }

  handleCepChange() {
    this.$.push(
      this.clientForm.controls.endereco.controls.cep.valueChanges
        .pipe(debounceTime(500))
        .subscribe((cep) => {
          if (cep?.length !== 8) return;
          this.fetchCep(cep);
        })
    );
  }

  handleCnpjChange() {
    this.$.push(
      this.clientForm.controls.cpfCnpj.valueChanges
        .pipe(debounceTime(500), skip(1))
        .subscribe((cnpj) => {
          const cnpjDigits = cnpj?.replace(/\D/g, "") || "";
          if (cnpjDigits.length !== 14) return;
          this.fetchAuxCompany(cnpjDigits);
        }),
      this.clientForm.controls.cpfCnpj.valueChanges.subscribe(() => {
        this.syncJuridicPerson();
      }),
      // Atualiza validação da inscrição estadual quando indicador muda
      this.clientForm.controls.indicadorInscricaoEstadual.valueChanges.subscribe(
        () => {
          this.clientForm.controls.inscricaoEstadual.updateValueAndValidity();
        }
      )
    );
  }

  /** Reaplica o validador de tamanho da razão social com o limite atual. */
  private applyRazaoSocialMaxLength() {
    const control = this.clientForm.controls.razaoSocial;
    control.setValidators([
      Validators.required,
      maxLengthValidator(this.razaoSocialMaxLength)
    ]);
    control.updateValueAndValidity();
  }

  /**
   * Mantém `juridicPerson` e a validação da IE alinhados ao CPF/CNPJ atual.
   * É chamado no init e após patch do @Input `cliente`, porque o valor
   * inicial (orçamento / payload) não dispara `valueChanges` — sem isso os
   * campos de PJ só apareciam depois de apagar e digitar o CNPJ de novo.
   */
  syncJuridicPerson() {
    const controls = this.clientForm.controls;
    const juridic = isCnpj(controls.cpfCnpj.value);

    this.juridicPerson.set(juridic);

    // Sem indicador informado, pré-seleciona o que o PlugNotas derivaria da IE
    // (1 com IE, 2 com ISENTO, 9 sem IE) para o usuário ver o que será enviado.
    if (
      juridic &&
      !normalizeIndIeDest(controls.indicadorInscricaoEstadual.value)
    ) {
      controls.indicadorInscricaoEstadual.setValue(
        deriveIndIeDest(controls.inscricaoEstadual.value)
      );
    }

    controls.inscricaoEstadual.updateValueAndValidity();
  }

  /**
   * Mensagem específica do erro atual da inscrição estadual.
   */
  inscricaoEstadualInvalidMessage(): string {
    const code = this.clientForm.controls.inscricaoEstadual.errors?.[
      "inscricaoEstadual"
    ] as InscricaoEstadualError | undefined;

    return code ? INSCRICAO_ESTADUAL_ERROR_MESSAGES[code] : "";
  }

  handleLogradouroChange() {
    this.$.push(
      this.clientForm.controls.endereco.controls.logradouro.valueChanges
        .pipe(debounceTime(500), skip(1))
        .subscribe((logradouro) => {
          const option = this.tipoLogradouroOptions.find((o) =>
            logradouro?.toLowerCase().includes(o.value.toLowerCase())
          );

          if (option) {
            this.clientForm
              .get("endereco")
              ?.get("tipoLogradouro")
              ?.setValue(option.value);
          }
        })
    );
  }

  handleFormChange() {
    this.$.push(
      this.clientForm.valueChanges.pipe(debounceTime(500)).subscribe((form) => {
        this.data.emit(form);
        if (!form.cpfCnpj) return;
      }),
      this.clientForm.statusChanges.subscribe((status) => {
        this.status.emit(status === "VALID");
      })
    );
  }

  fetchStates() {
    this.$.push(
      this.ibgeService.fetchStates().subscribe({
        next: (states) => {
          this.states.set(states);

          const state = this.budget()?.cliente.uf;

          if (state) {
            this.fetchCities(state);
          }
        }
      })
    );
  }

  fetchCities(uf: string) {
    this.$.push(
      this.ibgeService.fetchCities(uf).subscribe({
        next: (cities) => {
          this.cities.set(cities);
        }
      })
    );
  }

  fetchCep(cep: string) {
    if (cep.length !== 8) return;

    this.$.push(
      this.cepService.fetchCep(cep).subscribe({
        next: (address) => {
          this.endereco?.get("bairro")?.setValue(address.bairro);
          this.endereco?.get("logradouro")?.setValue(address.logradouro);
          this.endereco?.get("codigoCidade")?.setValue(address.ibge);
          this.endereco?.get("estado")?.setValue(address.uf);
          this.endereco?.get("descricaoCidade")?.setValue(address.municipio);
        },
        error: (err) => {
          this.modalService.error(err.error.error.message);
        }
      })
    );
  }

  hasMainAddressDataMissing() {
    const endereco = this.clientForm.controls.endereco.controls;

    return (
      !this.isAddressFieldFilled(endereco.logradouro.value) ||
      !this.isAddressFieldFilled(endereco.bairro.value) ||
      !this.isAddressFieldFilled(endereco.estado.value) ||
      !this.isAddressFieldFilled(endereco.descricaoCidade.value) ||
      !this.isAddressFieldFilled(endereco.codigoCidade.value)
    );
  }

  canSearchAddressInApi() {
    const cep =
      this.clientForm.controls.endereco.controls.cep.value?.replaceAll(
        /\D/g,
        ""
      );

    return !!cep && cep.length === 8 && !this.isSearchingAddress();
  }

  searchMissingAddressData() {
    if (!this.canSearchAddressInApi()) {
      this.modalService.error("Informe um CEP válido para buscar os dados.");
      return;
    }

    const cep =
      this.clientForm.controls.endereco.controls.cep.value?.replaceAll(
        /\D/g,
        ""
      );

    if (!cep) return;

    this.isSearchingAddress.set(true);

    this.$.push(
      this.cepService.fetchCep(cep).subscribe({
        next: (address) => {
          this.patchMissingAddressFields(address);
          this.isSearchingAddress.set(false);
        },
        error: (err) => {
          this.isSearchingAddress.set(false);
          this.modalService.error(err.error.error.message);
        }
      })
    );
  }

  private patchMissingAddressFields(address: {
    bairro?: string;
    logradouro?: string;
    ibge?: string;
    uf?: string;
    municipio?: string;
  }) {
    const endereco = this.clientForm.controls.endereco.controls;

    if (!this.isAddressFieldFilled(endereco.bairro.value)) {
      endereco.bairro.setValue(address.bairro || "");
    }

    if (!this.isAddressFieldFilled(endereco.logradouro.value)) {
      endereco.logradouro.setValue(address.logradouro || "");
    }

    if (!this.isAddressFieldFilled(endereco.codigoCidade.value)) {
      endereco.codigoCidade.setValue(address.ibge || "");
    }

    if (!this.isAddressFieldFilled(endereco.estado.value)) {
      endereco.estado.setValue(address.uf || "");
    }

    if (!this.isAddressFieldFilled(endereco.descricaoCidade.value)) {
      endereco.descricaoCidade.setValue(address.municipio || "");
    }
  }

  private isAddressFieldFilled(value: string | null | undefined) {
    return !!value && !!value.toString().trim();
  }

  /**
   * Formata CEP no padrão 00.000-000
   */
  private formatCep(cep: string): string {
    const cepLimpo = cep.replaceAll(/\D/g, "");
    if (cepLimpo.length === 8) {
      return cepLimpo.replace(/(\d{2})(\d{3})(\d{3})/, "$1.$2-$3");
    }
    return cepLimpo;
  }

  fetchAuxCompany(cnpj: string) {
    const cnpjDigits = cnpj.replace(/\D/g, "");

    this.$.push(
      this.companyService.fetchCnpj(cnpjDigits).subscribe({
        // A consulta passou a vir do nf-server (PlugNotas GET /cnpj) já no
        // formato do cadastro: razaoSocial, email e endereco.{cep,numero,...}.
        next: (aux) => {
          this.clientForm
            .get("razaoSocial")
            ?.setValue(
              aux.razaoSocial || this.clientForm.get("razaoSocial")?.value || ""
            );
          this.clientForm
            .get("email")
            ?.setValue(aux.email || this.clientForm.get("email")?.value || "");

          const endereco = this.clientForm.controls.endereco.controls;

          if (!this.isAddressFieldFilled(endereco.numero?.value?.toString())) {
            endereco.numero?.setValue(aux.endereco?.numero || "");
          }

          if (!this.isAddressFieldFilled(endereco.complemento?.value)) {
            endereco.complemento?.setValue(aux.endereco?.complemento || "");
          }

          const cep = aux.endereco?.cep;
          if (!cep) return;

          // Formata CEP removendo caracteres não numéricos e garantindo 8 dígitos
          const cepLimpo = cep.replaceAll(/\D/g, "");
          if (cepLimpo.length === 8) {
            // Formata CEP no padrão 00.000-000 antes de setar
            const cepFormatado = this.formatCep(cepLimpo);
            // SetValue com emitEvent: false para evitar múltiplos disparos
            this.endereco
              ?.get("cep")
              ?.setValue(cepFormatado, { emitEvent: false });
            // Busca o endereço do CEP usando apenas números
            this.fetchCep(cepLimpo);
          } else if (cepLimpo.length > 0) {
            // Se tiver CEP mas formato inválido, ainda seta o valor
            this.endereco?.get("cep")?.setValue(cepLimpo);
          }
        },
        error: (err) => {
          const message =
            err?.error?.error?.message ||
            err?.error?.message ||
            err?.message ||
            "Notamos uma instabilidade no servidor da receita federal, tente novamente mais tarde.";

          this.modalService.error(message);
        }
      })
    );
  }
}
